/**
 * Vehicle Ranking Page
 * Manage AI ranking weights and anti-abuse rules
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Slider } from '../../../components/ui/slider';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  ArrowLeft, TrendingUp, Save, Shield, Sparkles,
  AlertTriangle, Scale
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleRankingPage = () => {
  const [selectedCountry, setSelectedCountry] = useState('global');
  const [weights, setWeights] = useState({
    relevance_score: 30,
    price_fairness_score: 20,
    trust_score: 15,
    listing_quality_score: 10,
    freshness_score: 10,
    distance_score: 10,
    seller_responsiveness_score: 5
  });
  const [antiAbuse, setAntiAbuse] = useState({
    duplicate_detection: true,
    unrealistic_price: true,
    repeated_reposts: true,
    boost_limits: true,
    suspicious_patterns: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRankingConfig();
  }, [selectedCountry]);

  const fetchRankingConfig = async () => {
    try {
      const country = selectedCountry === 'global' ? null : selectedCountry;
      const response = await axios.get(`${API_URL}/api/admin/vehicles/ranking`, {
        params: { country }
      });
      if (response.data.config?.weights) {
        const w = response.data.config.weights;
        setWeights({
          relevance_score: Math.round((w.relevance_score || 0.3) * 100),
          price_fairness_score: Math.round((w.price_fairness_score || 0.2) * 100),
          trust_score: Math.round((w.trust_score || 0.15) * 100),
          listing_quality_score: Math.round((w.listing_quality_score || 0.1) * 100),
          freshness_score: Math.round((w.freshness_score || 0.1) * 100),
          distance_score: Math.round((w.distance_score || 0.1) * 100),
          seller_responsiveness_score: Math.round((w.seller_responsiveness_score || 0.05) * 100)
        });
      }
    } catch (error) {
      console.error('Failed to fetch ranking config:', error);
    }
  };

  const updateWeight = (key, value) => {
    setWeights(prev => ({ ...prev, [key]: value }));
  };

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const weightData = {};
      Object.entries(weights).forEach(([key, val]) => {
        weightData[key] = val / 100;
      });
      
      await axios.post(
        `${API_URL}/api/admin/vehicles/ranking?admin_id=admin`,
        weightData,
        { params: { country: selectedCountry === 'global' ? null : selectedCountry } }
      );
      alert('Ranking config saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const weightLabels = {
    relevance_score: { label: 'Relevance Score', description: 'How well listing matches user filters', icon: '🎯' },
    price_fairness_score: { label: 'Price Fairness', description: 'Comparison to market median', icon: '💰' },
    trust_score: { label: 'Trust Score', description: 'Verified seller, inspection, documents', icon: '✅' },
    listing_quality_score: { label: 'Listing Quality', description: 'Photos, description, completeness', icon: '⭐' },
    freshness_score: { label: 'Freshness', description: 'How recently created/updated', icon: '🆕' },
    distance_score: { label: 'Distance', description: 'Proximity to user location', icon: '📍' },
    seller_responsiveness_score: { label: 'Seller Responsiveness', description: 'Reply speed and rate', icon: '💬' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              Ranking & Search
            </h1>
            <p className="text-gray-500">Configure AI ranking weights and anti-abuse rules</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">🌍 Global</SelectItem>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 UK</SelectItem>
              <SelectItem value="EU">🇪🇺 EU</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={saveChanges} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ranking Weights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Ranking Weights
              </div>
              <Badge variant={totalWeight === 100 ? 'default' : 'destructive'}>
                Total: {totalWeight}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(weights).map(([key, value]) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span>{weightLabels[key]?.icon}</span>
                    <span>{weightLabels[key]?.label}</span>
                  </Label>
                  <span className="font-bold text-blue-600">{value}%</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{weightLabels[key]?.description}</p>
                <Slider
                  value={[value]}
                  onValueChange={([val]) => updateWeight(key, val)}
                  max={50}
                  step={1}
                  className="w-full"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Anti-Abuse Rules */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-500" />
              Anti-Abuse Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Duplicate Detection</p>
                <p className="text-sm text-gray-500">Hide duplicate listings automatically</p>
              </div>
              <Switch
                checked={antiAbuse.duplicate_detection}
                onCheckedChange={(v) => setAntiAbuse(p => ({ ...p, duplicate_detection: v }))}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Unrealistic Price Detection</p>
                <p className="text-sm text-gray-500">Flag listings with suspicious pricing</p>
              </div>
              <Switch
                checked={antiAbuse.unrealistic_price}
                onCheckedChange={(v) => setAntiAbuse(p => ({ ...p, unrealistic_price: v }))}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Repeated Repost Penalty</p>
                <p className="text-sm text-gray-500">Decay score for frequent reposts</p>
              </div>
              <Switch
                checked={antiAbuse.repeated_reposts}
                onCheckedChange={(v) => setAntiAbuse(p => ({ ...p, repeated_reposts: v }))}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Boost Limits</p>
                <p className="text-sm text-gray-500">Cap boost multiplier at 1.5x</p>
              </div>
              <Switch
                checked={antiAbuse.boost_limits}
                onCheckedChange={(v) => setAntiAbuse(p => ({ ...p, boost_limits: v }))}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Suspicious Pattern Detection</p>
                <p className="text-sm text-gray-500">Auto-flag for manual review</p>
              </div>
              <Switch
                checked={antiAbuse.suspicious_patterns}
                onCheckedChange={(v) => setAntiAbuse(p => ({ ...p, suspicious_patterns: v }))}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scoring Formula */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Scoring Formula
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <pre>{`score = 
  ${(weights.relevance_score / 100).toFixed(2)} * relevanceScore
+ ${(weights.price_fairness_score / 100).toFixed(2)} * priceFairnessScore
+ ${(weights.trust_score / 100).toFixed(2)} * trustScore
+ ${(weights.listing_quality_score / 100).toFixed(2)} * listingQualityScore
+ ${(weights.freshness_score / 100).toFixed(2)} * freshnessScore
+ ${(weights.distance_score / 100).toFixed(2)} * distanceScore
+ ${(weights.seller_responsiveness_score / 100).toFixed(2)} * sellerResponsivenessScore`}</pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleRankingPage;
